import { CustomError, Result, SessionProvider } from 'core/infrastructure';
import { Res, WhenAskForTableCompanions, Permission, WhenAskForFriendlyName, TicketDTO } from 'core/api';
import setFriendlyName from '../../main/utils/setFriendlyNameIfNeeded';
import setTableCompanionsIfNeeded from '../../main/utils/setTableCompanionsIfNeeded';
import { Message } from 'core/ui-next/modal';

async function tryToAssignFriendlyNameIfNeeded(params: CloseDocumentParams): Promise<boolean> {

	if (params.closeAs === 'Refund')
		return true;

	if (params.setFriendlyName === 'DISABLE_ASK_FOR_FRIENDLY_NAME')
		return true;

	const setFriendlyNameResult = await setFriendlyName({
		when: WhenAskForFriendlyName.OnCloseTicket,
		setFriendlyName: params.setFriendlyName,
		ticket: params.getTicket()
	});
	return setFriendlyNameResult.ok;
}

async function tryToAssignTableCompanionsIfNeeded(params: CloseDocumentParams): Promise<boolean> {

	if (params.closeAs === 'Refund')
		return true;

	if (params.setTableCompanions === 'DISABLE_SET_TABLE_COMPANIONS')
		return true;

	const setTableCompanionsResult = await setTableCompanionsIfNeeded({
		when: WhenAskForTableCompanions.OnCloseDocument,
		ticket: params.getTicket(),
		setTableCompanions: params.setTableCompanions
	});
	return setTableCompanionsResult.ok;
}

async function ensureConsumedCouponAreTriggeringOffers(ticket: TicketDTO): Promise<boolean> {
	const couponsWithoutOffer = ticket.Lines.filter(x => x.ConsumedCouponCode && !x.AppliedOfferCode);
	if (couponsWithoutOffer.length === 0)
		return true;

	const formattedCoupons = couponsWithoutOffer.map(x => `- ${x.Product.SaleFormatName} (${x.ConsumedCouponCode})`)
		.join('\n');

	const result = await Message.confirm(Res.Msg.MSG_WARN_CONSUMED_COUPONS_WITHOUT_TRIGGERING_OFFERS(formattedCoupons));
	return result.ok;
}

async function ensureCanBeClosedAsInvoice(params: CloseDocumentParams): Promise<boolean> {

	if (!SessionProvider.getCurrent().AllowCashTransactions) {
		await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
		return false;
	}

	if (!SessionProvider.hasPermission(Permission.Ticket_Close)) {
		await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.Ticket_Close)));
		return false;
	}

	if (params.getTicket().NetAmount < 0) {
		await Message.show(Res.Msg.MSG_CANNOT_CLOSE_TICKET_WITH_NEGATIVE_AMOUNT());
		return false;
	}

	if (params.getTicket().PendingAmount < 0) {
		await Message.show(Res.Msg.MSG_TO_CLOSE_NEGATIVE_DOCUMENT_REFUND_IS_NEEDED());
		return false;
	}

	if (!(await ensureConsumedCouponAreTriggeringOffers(params.getTicket())))
		return false;

	return true;
}


async function ensureCanBeClosedAsRefund(params: CloseDocumentParams): Promise<boolean> {

	if (!SessionProvider.getCurrent().AllowCashTransactions) {
		await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
		return false;
	}

	if (!SessionProvider.hasPermission(Permission.Ticket_Refund)) {
		await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.Ticket_Refund)));
		return false;
	}

	const ticket = params.getTicket();
	if (ticket.PrepaidAmount !== 0) {
		await Message.show(Res.Msg.MSG_CANNOT_CREATE_A_REFUND_WITH_PAYMENTS());
		return false;
	}

	return true;
}


async function ensureCanBeClosedAsDeliveryNote(params: CloseDocumentParams) {

	if (!SessionProvider.getCurrent().AllowCashTransactions) {
		await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
		return false;
	}

	if (!SessionProvider.hasPermission(Permission.DeliveryNote_Create)) {
		await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.DeliveryNote_Create)));
		return false;
	}

	if (params.getTicket().NetAmount < 0) {
		const result = await Message.confirm(Res.Msg.MSG_EMIT_REFUND_DELIVERY_NOTE());
		if (!result.ok)
			return false;
	}

	return true;
}

async function ensureCanBeClosedAsSalesOrder(params: CloseDocumentParams) {

	if (!SessionProvider.getCurrent().AllowCashTransactions) {
		await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
		return false;
	}

	if (!SessionProvider.hasPermission(Permission.SalesOrder_Create)) {
		await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.DeliveryNote_Create)));
		return false;
	}

	if (params.getTicket().NetAmount < 0) {
		const result = await Message.confirm(Res.Msg.MSG_EMIT_REFUND_SALES_ORDER());
		if (!result.ok)
			return false;
	}

	return true;
}


async function ensureCanBeClosed(params: CloseDocumentParams) {
	switch (params.closeAs) {
		case 'Invoice': return ensureCanBeClosedAsInvoice(params);
		case 'Refund': return ensureCanBeClosedAsRefund(params);
		case 'DeliveryNote': return ensureCanBeClosedAsDeliveryNote(params);
		case 'SalesOrder': return ensureCanBeClosedAsSalesOrder(params);
	}
}

type CloseDocumentParams = {

	closeAs: 'DeliveryNote' | 'SalesOrder' | 'Invoice' | 'Refund',
	getTicket: () => TicketDTO,
	setTableCompanions: ((tableCompanions: Nullable<number>) => Promise<Result<void, CustomError>>) | 'DISABLE_SET_TABLE_COMPANIONS',
	setFriendlyName: ((name: string) => Promise<void>) | 'DISABLE_ASK_FOR_FRIENDLY_NAME',
};

function minAmountIsValid(params: CloseDocumentParams): boolean {
	if (params.closeAs === 'Refund')
		return true;

	const ticket = params.getTicket();

	if (!ticket.TableCompanions)
		return true;

	if (ticket.NetAmount < 0)
		return true;

	return ticket.MinRequiredAmount <= ticket.NetAmount;
}

// Prepara un ticket para ser cerrado. Asegura que se puede cerrar (no es vacío, no está frozen, etc.),
// que el usuario tiene permisos, que se han establecido comensales, friendly name, cliente, etc.
export default async function makeTicketReadyToClose(params: CloseDocumentParams): Promise<Result> {

	if (params.getTicket().IsEmpty) {
		await Message.show(Res.Msg.MSG_TICKET_IS_EMPTY());
		return Result.error();
	}

	if (params.getTicket().IsFrozen) {
		await Message.show(Res.Msg.MSG_CANNOT_MODIFY_FROZEN_TICKET());
		return Result.error();
	}

	if (!(await ensureCanBeClosed(params)))
		return Result.error();

	if (!(await tryToAssignTableCompanionsIfNeeded(params)))
		return Result.error();

	if (!(await tryToAssignFriendlyNameIfNeeded(params)))
		return Result.error();

	if (!minAmountIsValid(params)) {
		await Message.show(Res.Msg.MSG_CANNOT_CLOSE_DOCUMENT_WITHOUT_MIN_REQUIRED_AMOUNT(params.getTicket().MinRequiredAmount));
		return Result.error();
	}

	return Result.ok();
}
